import React, { useMemo, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import TextAreaWithCharCounter from "@/main/react/component/TextAreaWithCharCounter";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { ErrorBanner } from "@/main/react/component/ErrorBanner";
import { InfoBanner } from "@/main/react/component/InfoBanner";
import type { Localizer } from "@/main/react/Types";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import logger from "@/lib/logger";
import { REQUEST_VALIDATION_LIMITS } from "@/main/validation/request";
import {
    DEFAULT_REPORT_REASON_TYPE,
    MODERATION_REPORT_REASON_DESCRIPTION_LOCALIZATION_MAP,
    MODERATION_REPORT_REASON_LOCALIZATION_MAP,
    ModerationReportApi,
    type ModerationReportReasonType,
    REPORTABLE_ENTITY_LOCALIZATION_MAP,
    reportReasonTypesFor,
    type ReportableEntityType,
} from "@/main/common/ModerationReportApi";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getSafeToastMessageFromError } from "@/main/common/ApiUtils";
import { ReportAttachmentPicker, type ReportAttachment } from "@/main/react/Dialog/ReportAttachmentPicker";

type Props = {
    entityType: ReportableEntityType;
    publicId: string;
    localizer: Localizer;
    onClose?: () => void;
    onSuccess?: () => void;
};

function validateReason(reason: string, localizer: Localizer): string | null {
    if (reason.trim().length > REQUEST_VALIDATION_LIMITS.longText.maxLength) {
        return localizer("report.modal.reason_too_long", REQUEST_VALIDATION_LIMITS.longText.maxLength);
    }

    return null;
}

export function ReportEntityDialogInner({ entityType, publicId, localizer, onClose, onSuccess }: Props) {
    /**
     * A direct message is reported through the same dialog as every other entity, with three
     * differences, all of which follow from the content living in a private two-party conversation:
     * two reasons are hidden, there is no screenshot picker (the evidence IS the conversation and the
     * server captures it verbatim), and the block toggle is present and CHECKED by default.
     *
     * One dialog rather than a sibling: the reason taxonomy, the validation, the duplicate-submit lock
     * and the success toasts are identical, and a second copy would drift from this one within a slice.
     */
    const isChatMessage = entityType === "chat_message";

    /**
     * A GROUP chat message shares two of those three differences and not the third, so it needs its own
     * predicate rather than joining `isChatMessage`.
     *
     * Same: the server captures a window of the conversation, so the disclosure is owed and the
     * screenshot picker must not appear. Different: no block toggle, because the room analogue of
     * blocking is banning and that is a leader's power, not a reporter's. Also different, and handled
     * in `reportReasonTypesFor` rather than here: a room gets the FULL reason list, because AI slop and
     * copyright are ordinary complaints in front of hundreds of people and meaningless between two.
     *
     * Splitting this out is what the two flags below are for. Folding it into `isChatMessage` would have
     * silently attached a block toggle to a report that has no block half; leaving it out of both — the
     * state this dialog was in for one commit — showed a screenshot picker whose files the group API arm
     * discards, and hid the one disclosure telling a member that ten messages travel with her report.
     */
    const isGroupChatMessage = entityType === "group_chat_message";
    /** Both chat kinds: the server takes an eleven-message window, so the member must be told. */
    const capturesConversation = isChatMessage || isGroupChatMessage;

    const [saving, setSaving] = useState(false);
    const [reasonType, setReasonType] = useState<ModerationReportReasonType>(DEFAULT_REPORT_REASON_TYPE);
    const [reason, setReason] = useState("");
    const [attachments, setAttachments] = useState<ReportAttachment[]>([]);
    // Checked by default for a DM, and only for a DM. A member reporting a message they RECEIVED has
    // already said the contact is unwanted; making them take a second action to stop it is the gap this
    // feature exists to close. `ReportUserDialog` leaves it off because reporting an account you found
    // on a profile page carries no such statement.
    const [blockSender, setBlockSender] = useState(isChatMessage);
    const [error, setError] = useState<string | null>(null);
    const submitLockRef = useRef(false);

    const reasonTypes = useMemo(() => reportReasonTypesFor(entityType), [entityType]);

    const entityLabel = useMemo(
        () => localizer(REPORTABLE_ENTITY_LOCALIZATION_MAP[entityType]),
        [entityType, localizer]
    );
    // Undefined for CSAE, which carries no gloss on purpose - see the map's own note. The paragraph
    // below still renders so its data-playwright-id stays a stable hook; it is simply empty.
    const selectedReasonDescriptionKey = MODERATION_REPORT_REASON_DESCRIPTION_LOCALIZATION_MAP[reasonType];
    const selectedReasonDescription = selectedReasonDescriptionKey ? localizer(selectedReasonDescriptionKey) : "";

    async function handleSubmit(event?: React.FormEvent) {
        event?.preventDefault();

        if (saving || submitLockRef.current) {
            return;
        }

        setError(null);

        const trimmedPublicId = publicId.trim();
        if (!trimmedPublicId) {
            setError(localizer("report.modal.invalid_target"));
            return;
        }

        const trimmedReason = reason.trim();
        const validationMessage = validateReason(trimmedReason, localizer);
        if (validationMessage) {
            setError(validationMessage);
            return;
        }

        submitLockRef.current = true;
        setSaving(true);
        try {
            const created = isChatMessage
                ? await ModerationReportApi.reportChatMessage(trimmedPublicId, trimmedReason, reasonType, blockSender)
                : await ModerationReportApi.report(
                    entityType,
                    trimmedPublicId,
                    trimmedReason,
                    reasonType,
                    attachments.map((attachment) => attachment.imagePublicId),
                );
            toast.success(localizer(created ? "report.modal.success" : "report.modal.success_duplicate"));
            onSuccess?.();
            onClose?.();
        } catch (err: any) {
            logger.error(`Failed reporting ${entityType}`, err);
            setError(getSafeToastMessageFromError(err, localizer("report.modal.failed")));
        } finally {
            submitLockRef.current = false;
            setSaving(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} noValidate className="w-full" data-playwright-id="report-entity-form">
            <div className="flex flex-col gap-4">
                {error ? <ErrorBanner description={error} className="rounded-xl py-3" /> : null}

                <InfoBanner
                    title={localizer("report.modal.guidance.title")}
                    description={localizer("report.modal.guidance.description", entityLabel)}
                    className="rounded-xl"
                />

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium" htmlFor="report-entity-reason-type">
                        {localizer("report.modal.reason_type.label")}
                    </label>
                    {/*
                     * Four deviations in one control before 2026-08-03, all in the same class string:
                     *  - a BARE `border`, which in Tailwind v4 resolves to `currentColor` — so this
                     *    select outlined itself in near-black while the textarea 90px below it drew
                     *    the ordinary hairline, and the two adjacent fields looked like they came
                     *    from different products;
                     *  - 39px tall on a coarse pointer, under the rule-2 floor;
                     *  - `rounded-md` where the input token is `rounded-lg`;
                     *  - no `focus-visible:` at all, so it fell back to the UA outline instead of the
                     *    one sanctioned ring.
                     * The label also had no `htmlFor` and did not wrap the control.
                     *
                     * The border then read the FILL token (`--input`, a background-ramp mix measuring
                     * 1.03-1.23:1 against its surface) until 2026-08-15 — a hairline no one could
                     * perceive, which is a different way to have no edge. It now reads the measured
                     * boundary token. Enforced by scripts/check-control-boundary-tokens.mjs, whose
                     * check C only learned to look outside `src/components/ui` in that same change.
                     */}
                    <select
                        id="report-entity-reason-type"
                        value={reasonType}
                        onChange={(event) => setReasonType(event.target.value as ModerationReportReasonType)}
                        data-playwright-id="report-entity-reason-type"
                        className="w-full rounded-lg border border-control-boundary bg-transparent px-3 py-2 text-sm [@media(pointer:coarse)]:text-base outline-none touch-manipulation [@media(pointer:coarse)]:min-h-11 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={saving}
                    >
                        {reasonTypes.map((value) => (
                            <option key={value} value={value}>
                                {localizer(MODERATION_REPORT_REASON_LOCALIZATION_MAP[value])}
                            </option>
                        ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                        {localizer("report.modal.reason_type.help")}
                    </p>
                    <p className="text-xs text-muted-foreground" data-playwright-id="report-entity-reason-description">
                        {selectedReasonDescription}
                    </p>
                </div>

                <div className="flex flex-col gap-1">
                    <TextAreaWithCharCounter
                        id="report-entity-reason"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        data-playwright-id="report-entity-reason"
                        floatingLabel={localizer("report.modal.reason.label")}
                        className="min-h-[80px]"
                        maxLength={REQUEST_VALIDATION_LIMITS.longText.maxLength}
                        disabled={saving}
                    />
                    <p className="text-xs text-muted-foreground">
                        {localizer("report.modal.reason.help")}
                    </p>
                </div>

                {/* The consent disclosure, and the one place in this dialog where rule 12's
                    "no mechanics in user copy" is deliberately set aside: a member is entitled to
                    know that ten of their OWN messages travel with the report, and a vague sentence
                    would not be worth saying. */}
                {/* Both chat kinds. The copy is already generic — "this message and the ten before
                    it" — so it is reused verbatim rather than forked; nothing in it says "yours". */}
                {capturesConversation ? (
                    <div data-playwright-id="report-entity-capture-notice">
                        <InfoBanner
                            description={localizer("report.modal.chat.capture_notice")}
                            className="rounded-xl"
                        />
                    </div>
                ) : null}

                {/* Absent, not disabled, for either chat kind: the evidence is the conversation,
                    captured verbatim by the server, and a picker here would invite a reporter to hand
                    us a cropped image of text we already hold in full. Neither endpoint has a field
                    for it, so a picker shown here would collect files and drop them. */}
                {capturesConversation ? null : (
                    <ReportAttachmentPicker
                        localizer={localizer}
                        attachments={attachments}
                        onChange={setAttachments}
                        disabled={saving}
                        idPrefix="report-entity"
                    />
                )}

                {isChatMessage ? (
                    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 rounded-lg border border-border/70 bg-background px-3 py-3">
                        <div className="flex h-9 items-center">
                            <Switch
                                checked={blockSender}
                                onCheckedChange={(checked) => setBlockSender(Boolean(checked))}
                                disabled={saving}
                                aria-label={localizer("report.modal.block.label")}
                                data-playwright-id="report-entity-block-toggle"
                            />
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-medium leading-5">
                                {localizer("report.modal.block.label")}
                            </div>
                            <div className="text-xs leading-5 text-muted-foreground">
                                {localizer("report.modal.block.description")}
                            </div>
                            {/* Blocking makes the thread unavailable in BOTH directions, so the
                                reporter loses their own view of the conversation. Saying so here is
                                what stops it reading as a bug when they go back to re-read it. */}
                            <div className="mt-1 text-xs leading-5 text-muted-foreground">
                                {localizer("report.modal.chat.block_notice")}
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Semantic tokens, not raw `var(--…)` arbitrary classes — theme-correct either way
                    today, but the raw form is invisible to any retune of the semantic token.
                    `--background` IS `--background1`, so `bg-background` is a pure rename. */}
                <div className="rounded-lg border border-border/70 bg-background px-3 py-2 text-xs text-muted-foreground">
                    {localizer("report.modal.contact_us_hint")}
                </div>
            </div>

            <SimpleDialogFooter>
                <CtaButton
                    type="button"
                    onClick={() => onClose?.()}
                    playwrightId="report-entity-cancel"
                    text={localizer("literal.cancel")}
                    extraClass=""
                    icon={X}
                    disabled={saving}
                    variant="outline"
                />

                <CtaButton
                    type="button"
                    onClick={() => handleSubmit()}
                    playwrightId="report-entity-submit"
                    text={localizer("report.modal.submit_button.entity", entityLabel)}
                    extraClass=""
                    icon={AlertTriangle}
                    disabled={saving}
                    variant="destructive"
                />
            </SimpleDialogFooter>
        </form>
    );
}

export function showReportEntityDialog(
    localizer: Localizer,
    entityType: ReportableEntityType,
    publicId: string,
    onSuccess?: () => void,
) {
    const entityLabel = localizer(REPORTABLE_ENTITY_LOCALIZATION_MAP[entityType]);
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="report-entity-modal-title" data-playwright-id="report-entity-title">{localizer("report.modal.title.entity", entityLabel)}</div>}
            width="md"
        >
            <ReportEntityDialogInner
                entityType={entityType}
                publicId={publicId}
                localizer={localizer}
                onClose={() => onOpenChange(false)}
                onSuccess={onSuccess}
            />
        </SimpleDialog>
    ));
}
